import React, { useEffect, useRef } from "react";

const AdmissionModal = ({ isVisible, onClose }) => {
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const backdropRef = useRef(null);
  const modalImageUrl =
    "https://res.cloudinary.com/ddvpczzng/image/upload/v1765064174/modal_lh7rt1.jpg";

  // Save the element that had focus before opening; restore on close
  useEffect(() => {
    if (isVisible) {
      previouslyFocusedRef.current = document.activeElement;
    } else if (!isVisible && previouslyFocusedRef.current) {
      // Defer to ensure DOM is updated
      setTimeout(() => {
        if (typeof previouslyFocusedRef.current.focus === "function") {
          previouslyFocusedRef.current.focus();
        }
      }, 0);
    }
  }, [isVisible]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isVisible) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  // Prevent body scroll when modal is open (preserve previous value)
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = prevOverflow || "";
    }
    return () => {
      document.body.style.overflow = prevOverflow || "";
    };
  }, [isVisible]);

  // Focus trap and initial focus
  useEffect(() => {
    if (!isVisible || !modalRef.current) return;

    const root = modalRef.current;
    const getFocusable = () =>
      root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

    // Move focus to the first focusable element
    const focusable = getFocusable();
    if (focusable.length) {
      focusable[0].focus();
    } else {
      root.setAttribute("tabindex", "-1");
      root.focus();
    }

    const handleTrap = (e) => {
      if (e.key !== "Tab") return;
      const els = getFocusable();
      if (!els.length) return;
      const first = els;
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    root.addEventListener("keydown", handleTrap);
    return () => root.removeEventListener("keydown", handleTrap);
  }, [isVisible]);

  // Close modal when clicking the backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="admission-title"
      aria-describedby="admission-actions"
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      } motion-reduce:transition-none`}
      onClick={handleBackdropClick}
    >
      {/* Modal Panel: Shrinks to fit the image. */}
      <div
        ref={modalRef}
        className={`relative w-full max-w-sm transform transition-transform duration-300 ${
          isVisible ? "scale-100" : "scale-95"
        } motion-reduce:transition-none motion-reduce:transform-none`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image drives the aspect; keep your styles intact */}
        <img
          id="admission-title"
          src={modalImageUrl}
          alt="St. Thomas Vidyalayam School - Admission Open Poster"
          className="w-full h-auto max-h-[calc(100vh-5rem)] object-contain rounded-2xl shadow-2xl"
          loading="lazy"
          decoding="async"
          width="600"
          height="800"
        />

        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 text-2xl font-bold text-white shadow-lg ring-2 ring-white/75 transition hover:scale-110 hover:bg-red-700 active:scale-95 motion-reduce:transition-none"
        >
          &times;
        </button>

        {/* Register Button */}
        <div
          id="admission-actions"
          className="absolute bottom-5 left-1/2 w-full max-w-[60%] -translate-x-1/2 px-4"
        >
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            aria-label="Register now (opens Google Form in a new tab)"
          >
            <button
              type="button"
              className="w-full rounded-lg bg-yellow-400 py-3 font-bold text-gray-900 shadow-lg transition hover:scale-105 hover:bg-yellow-500 active:scale-95 motion-reduce:transition-none"
            >
              Register Now
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdmissionModal;
